import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Grid,
  GridItem,
  Select,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { PhoneIcon, RepeatIcon } from "@chakra-ui/icons";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../services/api";
import { getSetting } from "../../services/settingService"; // مسیر صحیح سرویس را بررسی کنید
import { getCaptcha } from "../../services/authService";

export default function ClientRegister() {
  const [step, setStep] = useState(1); // 1: فرم ثبت نام، 2: تایید OTP
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [clientData, setClientData] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [isLoadingConsultants, setIsLoadingConsultants] = useState(false);
  const [captchaSvg, setCaptchaSvg] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  // ---------- state های مربوط به تنظیمات ----------
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  // -------------------------------------------

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const bgColor = useColorModeValue("white", "gray.700");
  const captchaBorderColor = useColorModeValue("gray.200", "gray.500");

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const res = await getCaptcha();
      setCaptchaSvg(res.data);
      setCaptchaToken(res.token);
    } catch (e) {
      toast.error("دریافت کپچا ناموفق بود");
    } finally {
      setCaptchaLoading(false);
    }
  };

  // ---------- دریافت تنظیمات و بررسی وضعیت ثبت نام ----------
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSetting();
        const general = response.data.general;
        // اگر disabledRgistration برابر true باشد، ثبت نام بسته است
        setIsRegistrationOpen(!general.disabledRgistration);
      } catch (error) {
        toast.error("دریافت تنظیمات با خطا مواجه شد");
        // در صورت خطا برای حفظ دسترسی، ثبت نام را باز می‌گذاریم
        setIsRegistrationOpen(true);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);
  // -------------------------------------------

  useEffect(() => {
    if (!settingsLoading && isRegistrationOpen) {
      fetchConsultants();
      fetchCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- بارگذاری یک‌بار پس از باز بودن ثبت‌نام
  }, [settingsLoading, isRegistrationOpen]);

  // دریافت لیست مشاوران
  const fetchConsultants = async () => {
    try {
      setIsLoadingConsultants(true);
      const response = await api.get("/users/consultants/public");
      const result = response.data;
      if (result.status === "success") {
        setConsultants(result.data.consultants);
      }
    } catch (error) {
      console.error("Error fetching consultants:", error);
    } finally {
      setIsLoadingConsultants(false);
    }
  };

  // ارسال کد تایید
  const sendOTP = async (phone, captchaInputVal, captchaTokenVal) => {
    try {
      const response = await api.post("/auth/send-otp-public", {
        phone,
        captchaInput: captchaInputVal,
        captchaToken: captchaTokenVal,
      });
      if (response.data.status === "success") {
        toast.success("کد تایید ارسال شد");
        return true;
      }
      toast.error(response.data.message || "خطا در ارسال کد تایید");
      return false;
    } catch (error) {
      console.error("Send OTP error:", error);
      const msg =
        error.response?.data?.message || "خطا در ارسال کد تایید";
      toast.error(msg);
      return false;
    }
  };

  // مرحله اول: ثبت اطلاعات و ارسال OTP
  const onSubmitStep1 = async (data) => {
    setIsLoading(true);
    setError("");

    try {
      if (!captchaInput.trim()) {
        setError("لطفاً متن کپچا را وارد کنید");
        return;
      }

      if (
        !data.firstName?.trim() ||
        !data.lastName?.trim() ||
        !data.nationalId?.trim() ||
        !data.phone?.trim() ||
        !data.address?.trim() ||
        !data.occupation?.trim() ||
        !data.birthYear?.toString().trim() ||
        !data.birthMonth?.toString().trim() ||
        !data.birthDay?.toString().trim() ||
        !data.consultant?.trim()
      ) {
        setError("لطفاً تمام فیلدهای اجباری را پر کنید");
        return;
      }

      const birthYear = data.birthYear.toString().trim();
      const birthMonth = data.birthMonth.toString().trim().padStart(2, "0");
      const birthDay = data.birthDay.toString().trim().padStart(2, "0");
      const birthDate = `${birthYear}/${birthMonth}/${birthDay}`;

      const year = parseInt(birthYear);
      const month = parseInt(birthMonth);
      const day = parseInt(birthDay);

      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        setError("تاریخ تولد وارد شده معتبر نیست");
        return;
      }

      if (month < 1 || month > 12 || day < 1 || day > 31) {
        setError("تاریخ تولد وارد شده معتبر نیست");
        return;
      }

      const currentYear = new Date().getFullYear();
      const gregorianYear = year + 621;
      const age = currentYear - gregorianYear;
      if (age < 1 || age > 120) {
        setError("سن باید بین 1 تا 120 سال باشد");
        return;
      }

      // بررسی تکراری نبودن شماره تلفن
      const checkPhoneResponse = await api.post("/clients/check-phone", {
        phone: data.phone,
      });
      if (
        checkPhoneResponse.data.data &&
        checkPhoneResponse.data.data.exists
      ) {
        setError("مراجعی با این شماره تلفن قبلاً ثبت شده است");
        return;
      }

      const otpSent = await sendOTP(data.phone, captchaInput, captchaToken);
      if (!otpSent) {
        fetchCaptcha();
        setCaptchaInput("");
        return;
      }

      const finalData = {
        ...data,
        birthDate: birthDate,
      };

      setClientData(finalData);
      setCaptchaInput("");
      await fetchCaptcha();
      setStep(2);
    } catch (error) {
      setError("خطا در ارسال کد تایید");
      toast.error("خطا در ارسال کد تایید");
    } finally {
      setIsLoading(false);
    }
  };

  // مرحله دوم: تایید OTP و ثبت نام نهایی
  const onSubmitStep2 = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError("لطفاً کد 6 رقمی را وارد کنید");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await api.post("/auth/verify-otp-public", {
        phone: clientData.phone,
        otp: otpCode,
      });
      if (response.data.status !== "success") {
        setError("کد تایید نامعتبر است");
        return;
      }

      const requestData = {
        firstName: clientData.firstName.trim(),
        lastName: clientData.lastName.trim(),
        nationalId: clientData.nationalId.trim(),
        phone: clientData.phone.trim(),
        address: clientData.address.trim(),
        occupation: clientData.occupation.trim(),
        birthDate: clientData.birthDate,
        consultant: clientData.consultant,
      };

      const response2 = await api.post("/clients/register", requestData);
      const result = response2.data;
      if (result.status === "success") {
        toast.success("ثبت نام با موفقیت انجام شد");
        navigate("/client");
      } else {
        setError(result.message || "خطا در ثبت نام");
      }
    } catch (error) {
      setError("خطا در ثبت نام");
      toast.error("خطا در ثبت نام");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- نمایش وضعیت بارگذاری تنظیمات ----------
  if (settingsLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <Spinner size="xl" />
        <Text ml={3}>در حال بارگذاری...</Text>
      </Flex>
    );
  }
  // -------------------------------------------

  // ---------- اگر ثبت نام بسته باشد ----------
  if (!isRegistrationOpen) {
    return (
      
      <Flex
  minH="100vh"
  align="center"
  justify="center"
  direction="column"
  bg={"gray.50"}
>
  <Alert
    status="warning"
    maxW="md"
    borderRadius="md"
    dir="rtl"
    justifyContent="center"
    fontSize="lg"
    py={4}
    mb={4}
  >
    <AlertIcon />
    ثبت نام موقتاً بسته شده است
  </Alert>
  <Button
    onClick={() => navigate("/client")}
    variant="outline"
    colorScheme="gray"
  >
    بازگشت
  </Button>
</Flex>
      
      
    );
  }
  // -------------------------------------------

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={"gray.50"}
    >
      <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6}>
        <Stack align="center">
          <Heading fontSize="4xl" textAlign="center">
            {step === 1 ? "ثبت نام مراجع" : "تایید شماره تلفن"}
          </Heading>
          <Text fontSize="lg" color="gray.600">
            {step === 1
              ? "برای ثبت نام در سیستم، اطلاعات خود را وارد کنید"
              : "کد تایید ارسال شده را وارد کنید"}
          </Text>
        </Stack>

        <Box rounded="lg" bg={bgColor} boxShadow="lg" p={8}>
          {step === 1 ? (
            <form onSubmit={handleSubmit(onSubmitStep1)}>
              <VStack spacing={4}>
                {error && (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                <FormControl id="firstName" isInvalid={errors.firstName}>
                  <FormLabel>نام *</FormLabel>
                  <Input
                    type="text"
                    {...register("firstName", {
                      required: "نام الزامی است",
                      minLength: {
                        value: 2,
                        message: "نام باید حداقل 2 کاراکتر باشد",
                      },
                      maxLength: {
                        value: 50,
                        message: "نام نمی‌تواند بیشتر از 50 کاراکتر باشد",
                      },
                      pattern: {
                        value: /^[\u0600-\u06FF\s]+$/,
                        message: "نام باید فقط شامل حروف فارسی باشد",
                      },
                    })}
                    placeholder="نام خود را وارد کنید"
                  />
                  {errors.firstName && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.firstName.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="lastName" isInvalid={errors.lastName}>
                  <FormLabel>نام خانوادگی *</FormLabel>
                  <Input
                    type="text"
                    {...register("lastName", {
                      required: "نام خانوادگی الزامی است",
                      minLength: {
                        value: 2,
                        message: "نام خانوادگی باید حداقل 2 کاراکتر باشد",
                      },
                      maxLength: {
                        value: 50,
                        message:
                          "نام خانوادگی نمی‌تواند بیشتر از 50 کاراکتر باشد",
                      },
                      pattern: {
                        value: /^[\u0600-\u06FF\s]+$/,
                        message: "نام خانوادگی باید فقط شامل حروف فارسی باشد",
                      },
                    })}
                    placeholder="نام خانوادگی خود را وارد کنید"
                  />
                  {errors.lastName && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.lastName.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="nationalId" isInvalid={errors.nationalId}>
                  <FormLabel>کد ملی *</FormLabel>
                  <Input
                    type="text"
                    {...register("nationalId", {
                      required: "کد ملی الزامی است (با اعداد لاتین)",
                      pattern: {
                        value: /^\d{10}$/,
                        message:
                          "کد ملی باید دقیقاً 10 رقم و با اعداد لاتین باشد",
                      },
                      validate: (value) => {
                        if (!value) return "کد ملی الزامی است (با اعداد لاتین)";
                        if (!/^\d{10}$/.test(value)) {
                          return "کد ملی باید دقیقاً 10 رقم و با اعداد لاتین باشد";
                        }
                        const digits = value.split("").map(Number);
                        const checkDigit = digits[9];
                        const sum = digits
                          .slice(0, 9)
                          .reduce((acc, digit, index) => {
                            return acc + digit * (10 - index);
                          }, 0);
                        const remainder = sum % 11;
                        const calculatedCheckDigit =
                          remainder < 2 ? remainder : 11 - remainder;

                        if (checkDigit !== calculatedCheckDigit) {
                          return "کد ملی وارد شده معتبر نیست";
                        }
                        return true;
                      },
                    })}
                    placeholder="1234567890"
                    maxLength={10}
                  />
                  {errors.nationalId && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.nationalId.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="address" isInvalid={errors.address}>
                  <FormLabel>آدرس *</FormLabel>
                  <Input
                    type="text"
                    {...register("address", {
                      required: "آدرس الزامی است",
                      minLength: {
                        value: 10,
                        message: "آدرس باید حداقل 10 کاراکتر باشد",
                      },
                    })}
                    placeholder="آدرس کامل خود را وارد کنید"
                  />
                  {errors.address && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.address.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="occupation" isInvalid={errors.occupation}>
                  <FormLabel>شغل *</FormLabel>
                  <Input
                    type="text"
                    {...register("occupation", {
                      required: "شغل الزامی است",
                      minLength: {
                        value: 2,
                        message: "شغل باید حداقل 2 کاراکتر باشد",
                      },
                    })}
                    placeholder="شغل خود را وارد کنید"
                  />
                  {errors.occupation && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.occupation.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="consultant" isInvalid={errors.consultant}>
                  <FormLabel>مشاور *</FormLabel>
                  <Select
                    {...register("consultant", {
                      required: "انتخاب مشاور الزامی است",
                    })}
                    placeholder={
                      isLoadingConsultants
                        ? "در حال بارگذاری..."
                        : "انتخاب مشاور"
                    }
                    isDisabled={isLoadingConsultants || consultants.length === 0}
                  >
                    {consultants.map((consultant) => (
                      <option key={consultant._id} value={consultant._id}>
                        {consultant.name}
                      </option>
                    ))}
                  </Select>
                  {consultants.length === 0 && !isLoadingConsultants && (
                    <Text color="orange.600" fontSize="sm" mt={1}>
                      مشاوری برای انتخاب موجود نیست؛ لطفاً بعداً تلاش کنید یا با مرکز تماس بگیرید.
                    </Text>
                  )}
                  {errors.consultant && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.consultant.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="phone" isInvalid={errors.phone}>
                  <FormLabel>شماره تلفن *</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <PhoneIcon color="gray.300" />
                    </InputLeftElement>
                    <Input
                      type="tel"
                      {...register("phone", {
                        required: "شماره تلفن الزامی است",
                        pattern: {
                          value: /^09\d{9}$/,
                          message:
                            "شماره تلفن باید با 09 شروع شود و 11 رقم باشد",
                        },
                        validate: (value) => {
                          if (!value) return "شماره تلفن الزامی است";
                          if (!/^09\d{9}$/.test(value)) {
                            return "شماره تلفن باید با 09 شروع شود و 11 رقم باشد";
                          }
                          return true;
                        },
                      })}
                      placeholder="09123456789"
                      dir="ltr"
                    />
                  </InputGroup>
                  {errors.phone && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.phone.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="birthDate" isInvalid={errors.birthDate}>
                  <FormLabel>تاریخ تولد *</FormLabel>
                  <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                    <GridItem>
                      <Input
                        {...register("birthYear", {
                          required: "سال تولد الزامی است",
                          min: {
                            value: 1300,
                            message: "سال باید بعد از 1300 باشد",
                          },
                          max: {
                            value: 1410,
                            message: "سال باید قبل از 1410 باشد",
                          },
                          validate: (value) => {
                            if (!value || value.toString().trim() === "")
                              return "سال تولد الزامی است";
                            const year = parseInt(value);
                            if (isNaN(year) || year < 1300 || year > 1410) {
                              return "سال تولد باید بین 1300 تا 1410 باشد";
                            }
                            return true;
                          },
                        })}
                        placeholder="سال"
                        type="number"
                        min="1300"
                        max="1410"
                      />
                    </GridItem>
                    <GridItem>
                      <Input
                        {...register("birthMonth", {
                          required: "ماه تولد الزامی است",
                          min: { value: 1, message: "ماه باید بین 1 تا 12 باشد" },
                          max: { value: 12, message: "ماه باید بین 1 تا 12 باشد" },
                          validate: (value) => {
                            if (!value || value.toString().trim() === "")
                              return "ماه تولد الزامی است";
                            const month = parseInt(value);
                            if (isNaN(month) || month < 1 || month > 12) {
                              return "ماه تولد باید بین 1 تا 12 باشد";
                            }
                            return true;
                          },
                        })}
                        placeholder="ماه"
                        type="number"
                        min="1"
                        max="12"
                      />
                    </GridItem>
                    <GridItem>
                      <Input
                        {...register("birthDay", {
                          required: "روز تولد الزامی است",
                          min: { value: 1, message: "روز باید بین 1 تا 31 باشد" },
                          max: { value: 31, message: "روز باید بین 1 تا 31 باشد" },
                          validate: (value) => {
                            if (!value || value.toString().trim() === "")
                              return "روز تولد الزامی است";
                            const day = parseInt(value);
                            if (isNaN(day) || day < 1 || day > 31) {
                              return "روز تولد باید بین 1 تا 31 باشد";
                            }
                            return true;
                          },
                        })}
                        placeholder="روز"
                        type="number"
                        min="1"
                        max="31"
                      />
                    </GridItem>
                  </Grid>
                  {errors.birthDate && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.birthDate.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id="captcha">
                  <FormLabel>کد امنیتی</FormLabel>
                  <HStack spacing={2} align="start">
                    <Box
                      border="1px solid"
                      borderColor={captchaBorderColor}
                      borderRadius="md"
                      p={2}
                      bg="gray.50"
                      height="60px"
                      minW="160px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      dangerouslySetInnerHTML={{ __html: captchaSvg }}
                    />
                    <IconButton
                      aria-label="تازه‌سازی کپچا"
                      icon={
                        captchaLoading ? <Spinner size="sm" /> : <RepeatIcon />
                      }
                      onClick={fetchCaptcha}
                      isLoading={captchaLoading}
                      variant="outline"
                      colorScheme="blue"
                      size="md"
                    />
                  </HStack>
                  <Input
                    mt={2}
                    placeholder="متن تصویر را وارد کنید"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    autoComplete="off"
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="teal"
                  size="lg"
                  fontSize="md"
                  isLoading={isLoading}
                  loadingText="در حال ارسال کد تایید..."
                  w="full"
                >
                  ارسال کد تایید
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/client")}
                  size="md"
                >
                  بازگشت به صفحه ورود
                </Button>
              </VStack>
            </form>
          ) : (
            <VStack spacing={6}>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <Text fontSize="md" textAlign="center">
                کد تایید 6 رقمی به شماره {clientData?.phone} ارسال شد
              </Text>

              <FormControl>
                <FormLabel>کد تایید</FormLabel>
                <Input
                  type="text"
                  placeholder="کد 6 رقمی را وارد کنید"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setOtpCode(value);
                  }}
                  maxLength={6}
                  dir="ltr"
                  textAlign="center"
                  fontSize="lg"
                />
              </FormControl>

              <Box
                w="full"
                p={3}
                bg="green.50"
                borderRadius="md"
                border="1px"
                borderColor="green.200"
              >
                <Text fontWeight="bold" color="green.700" textAlign="center">
                  مشاور انتخاب‌شده:{" "}
                  {consultants.find((c) => c._id === clientData?.consultant)
                    ?.name || "—"}
                </Text>
              </Box>

              <FormControl id="captcha-step2">
                <FormLabel>کد امنیتی (برای ارسال مجدد پیامک)</FormLabel>
                <HStack spacing={2} align="start">
                  <Box
                    border="1px solid"
                    borderColor={captchaBorderColor}
                    borderRadius="md"
                    p={2}
                    bg="gray.50"
                    height="60px"
                    minW="160px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                    dangerouslySetInnerHTML={{ __html: captchaSvg }}
                  />
                  <IconButton
                    aria-label="تازه‌سازی کپچا"
                    icon={
                      captchaLoading ? <Spinner size="sm" /> : <RepeatIcon />
                    }
                    onClick={fetchCaptcha}
                    isLoading={captchaLoading}
                    variant="outline"
                    colorScheme="blue"
                    size="md"
                  />
                </HStack>
                <Input
                  mt={2}
                  placeholder="متن تصویر را وارد کنید"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  autoComplete="off"
                />
              </FormControl>

              <Button
                variant="link"
                color="blue.500"
                fontSize="sm"
                onClick={async () => {
                  if (!captchaInput.trim()) {
                    toast.error("لطفاً متن کپچا را برای ارسال مجدد وارد کنید");
                    return;
                  }
                  const ok = await sendOTP(
                    clientData.phone,
                    captchaInput,
                    captchaToken
                  );
                  if (!ok) {
                    fetchCaptcha();
                    setCaptchaInput("");
                  }
                }}
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                ارسال مجدد کد تایید
              </Button>

              <Stack spacing={3} w="full">
                <Button
                  colorScheme="teal"
                  size="lg"
                  isLoading={isLoading}
                  loadingText="در حال تایید..."
                  onClick={onSubmitStep2}
                  w="full"
                >
                  تایید و ثبت نام
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setOtpCode("");
                    fetchCaptcha();
                    setCaptchaInput("");
                  }}
                  size="md"
                  w="full"
                >
                  بازگشت
                </Button>
              </Stack>
            </VStack>
          )}
        </Box>
      </Stack>
    </Flex>
  );
}